import * as React from 'react';
import * as CodeMirror from 'codemirror';
import * as classNames from 'classnames';
import debounce = require('lodash/debounce');
import isNull = require('lodash/isNull');
import './plugins';
import './styles.sass';

interface ISessionEditorCodepadProps {
  initialCode: string;
  className?: string;
  children?: any;
  value: string;
  onChange?: (value?: any) => void;
}

interface ICodeMirror {
  language: string;
  value?: string;
}

const MODES = {
  'Plain Text': 'text/plain',
  'C': 'text/x-csrc',
  'C#': 'text/x-csharp',
  'C++': 'text/x-c++src',
  'Clojure': 'text/x-clojure',
  'Go': 'text/x-go',
  'HTML': 'text/html',
  'Haskell': 'text/x-haskell',
  'Java': 'text/x-java',
  'JavaScript': 'javascript',
  'PHP': 'text/x-php',
  'Python': 'python',
  'Ruby': 'text/x-ruby',
  'Swift': 'text/x-swift',
};
const DEBOUNCE_TIME: number = 100;

class SessionEditorCodemirror extends React.Component<ISessionEditorCodepadProps, any> {
  private editorRef: HTMLElement;
  private codeMirror: CodeMirror;
  private onChange: any = debounce(({doc: editor}) => this.change(editor), DEBOUNCE_TIME);

  public render(): React.ReactElement<any> {
    const {
      className,
      children,
    } = this.props;

    return (
      <div className={classNames('session-editor-codemirror', className)}>
        <div
          ref={node => this.editorRef = node}
          className={'session-editor-codemirror__container'}
        />

        {children}
      </div>
    );
  }

  public componentWillUnmount(): void {
    this.destroy();
  }

  public componentDidUpdate(): void {
    this.updateValue();
  }

  public define = ({language, value = ''}: ICodeMirror): CodeMirror => {
    const options = {
      gutters: [
        'CodeMirror-linenumbers',
        'CodeMirror-foldgutter',
      ],
      mode: MODES[language],
      autoCloseBrackets: true,
      styleActiveLine: true,
      matchBrackets: true,
      lineWrapping: true,
      lineNumbers: true,
      foldGutter: true,
      theme: 'monokai',
      tabSize: 2,
      extraKeys: this.extraKeys(),
      value,
    };

    this.codeMirror = (CodeMirror as any)(this.editorRef, options);
    this.codeMirror.on('change', this.onChange);
    this.props.onChange(this.codeMirror.getValue());

    return this.codeMirror;
  };

  public destroy = (): void => {
    if (this.codeMirror) {
      this.codeMirror.off('change', this.onChange);
    }
    this.codeMirror = null;
    this.editorRef.innerHTML = '';
  }

  public focusCursor = (): void => {
    this.codeMirror.focus();
    this.codeMirror.setCursor(0);
  }

  private extraKeys = () => {
    return {
      'Tab': (cm) => (
        cm.somethingSelected()
          ? cm.indentSelection('add')
          : cm.execCommand('insertSoftTab')
      ),
      'Shift-Tab': (cm) => cm.indentSelection('subtract'),
    };
  }

  private updateValue = () => {
    if (!this.codeMirror) {
      return;
    }

    const {value, initialCode} = this.props;
    if (isNull(value)) {
      this.codeMirror.setValue(initialCode);
      this.focusCursor();
    }
  }

  private change = (editor): void => {
    this.props.onChange(editor.getValue());
  }
}

export {
  CodeMirror,
  SessionEditorCodemirror,
};
